import * as React from "react";

const scriptId = "cloudflare-turnstile-script";
const scriptUrl = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
let scriptPromise: Promise<void> = null;

interface Props {
	siteKey: string;
	onTokenChange: (token: string) => void;
	onError: () => void;
}

/**
 * Plain Turnstile integration for React 16.2. The script is loaded only on the public signup page.
 */
export default class TurnstileWidget extends React.Component<Props, {}> {
	private container: HTMLDivElement;
	private widgetId: string | number;
	private mounted: boolean = false;

	componentDidMount() {
		this.mounted = true;
		if (this.props.siteKey) {
			this.loadScript().then(() => this.renderWidget()).catch(this.props.onError);
		}
	}

	componentWillUnmount() {
		this.mounted = false;
		if (window.turnstile && this.widgetId !== undefined) {
			window.turnstile.remove(this.widgetId);
		}
	}

	render() {
		return <div className="cf-turnstile"
					data-action="turnstile-spin-v2"
					ref={(element) => this.container = element}/>;
	}

	public reset() {
		this.props.onTokenChange("");
		if (window.turnstile && this.widgetId !== undefined) {
			window.turnstile.reset(this.widgetId);
		}
	}

	private renderWidget() {
		if (!this.mounted || !this.container || !window.turnstile || this.widgetId !== undefined) {
			return;
		}
		this.widgetId = window.turnstile.render(this.container, {
			sitekey: this.props.siteKey,
			action: "turnstile-spin-v2",
			callback: (token: string) => this.props.onTokenChange(token),
			"expired-callback": () => this.props.onTokenChange(""),
			// Deliberately does not raise onError. Turnstile retries on its own and usually succeeds a
			// moment later, but the page-level message this used to put up stayed there afterwards -
			// which is the "flags an issue the first time you open it, then fine" that was reported.
			// A check that genuinely never completes still blocks the form, and says so next to the
			// widget rather than as an alarm at the top of the page. onError is left for the one case
			// it was named for: the script not loading at all, where there is no widget to complete.
			// The code is logged because it is the only way to tell a passing hiccup from a
			// misconfigured key without guessing.
			"error-callback": (code: string) => {
				console.warn("Cloudflare Turnstile reported error", code);
				this.props.onTokenChange("");
			}
		});
	}

	private loadScript(): Promise<void> {
		if (window.turnstile) {
			return Promise.resolve();
		}
		if (scriptPromise) {
			return scriptPromise;
		}

		scriptPromise = new Promise<void>((resolve, reject) => {
			const existing = document.getElementById(scriptId) as HTMLScriptElement;
			const script = existing || document.createElement("script");
			const onLoad = () => resolve();
			const onError = () => {
				scriptPromise = null;
				reject(new Error("Cloudflare Turnstile could not be loaded"));
			};

			script.addEventListener("load", onLoad);
			script.addEventListener("error", onError);
			if (!existing) {
				script.id = scriptId;
				script.src = scriptUrl;
				script.async = true;
				script.defer = true;
				document.head.appendChild(script);
			}
		});
		return scriptPromise;
	}
}
